import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, CreditCard, Loader2 } from 'lucide-react';
import { authAPI } from '../../services/api';
import JourneyLayout from '../../components/JourneyLayout';
import { eventToRouteMap } from '../../Utils/constants';

function BankingDetails() {
  const [formData, setFormData] = useState({
    account_number: '',
    confirm_account_number: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [ifsc, setIfsc] = useState('');
  const [accountBranch, setAccountBranch] = useState('');
  const [leadId, setLeadId] = useState('');
  const [checkingAuth, setCheckingAuth] = useState(true);
  const navigate = useNavigate();
  const profileId = localStorage.getItem('profile_id') || '';

  useEffect(() => {
    const checkLoginStatus = async () => {
      setCheckingAuth(true);
      const token = localStorage.getItem('token') || 
                    localStorage.getItem('authToken') || 
                    localStorage.getItem('authtoken');
      
      if (!token || !profileId) {
        navigate('/apply-now');
        return;
      }

      // Fetch dashboard data to get IFSC and lead_id
      try {
        const dashboardResponse = await authAPI.getDashboardData(profileId);
        
        if (dashboardResponse.Status === 1) {
          const fetchedIfsc = dashboardResponse.Data?.customer_banking_details?.ifsc_code || '';
          const fetchedAccountBranch = dashboardResponse.Data?.customer_banking_details?.branch || '';
          const fetchedLeadId = dashboardResponse.Data?.profile_details?.lead_id || '';
          setIfsc(fetchedIfsc);
          setAccountBranch(fetchedAccountBranch);
          setLeadId(fetchedLeadId);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setCheckingAuth(false);
      }
    };

    checkLoginStatus();
  }, [profileId, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Only allow numbers for account number fields
    if (name === 'account_number' || name === 'confirm_account_number') {
      const numericValue = value.replace(/\D/g, '');
      setFormData((prev) => ({
        ...prev,
        [name]: numericValue,
      }));
    } else {
      // For other fields, use value as is
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
    setErrors({ ...errors, [name]: '', api: '' });
  };

  const handleBranchChange = (e) => {
    const value = e.target.value;
    // Allow alphabets, numbers, spaces, and common address characters
    const processedValue = value.replace(/[^A-Za-z0-9\s,.-]/g, '');
    setAccountBranch(processedValue);
    setErrors({ ...errors, branch: '', api: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    // Validate account number
    if (!formData.account_number.trim()) {
      newErrors.account_number = 'Account number is required';
    } else if (formData.account_number.trim().length < 9) {
      newErrors.account_number = 'Account number must be at least 9 digits';
    }

    // Validate confirm account number
    if (!formData.confirm_account_number.trim()) {
      newErrors.confirm_account_number = 'Please confirm your account number';
    } else if (formData.account_number !== formData.confirm_account_number) {
      newErrors.confirm_account_number = 'Account numbers do not match';
    }

    // Validate branch
    if (!accountBranch.trim()) {
      newErrors.branch = 'Bank branch is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      // Use the IFSC and lead_id fetched from useEffect
      if (!leadId) {
        setErrors({ api: 'Failed to fetch required data. Please refresh the page and try again.' });
        setLoading(false);
        return;
      }

      // Call save banking details API
      const response = await authAPI.saveBankingDetails({
        profile_id: profileId,
        lead_id: leadId,
        account_number: formData.account_number.trim(),
        confirm_account_number: formData.confirm_account_number.trim(),
        account_ifsc: ifsc,
        account_branch: accountBranch.trim(),
      });

      if (response.Status === 1) {
        // Navigate to e-kyc page
        const nextEvent = response.Data?.next_step;
        if (nextEvent && eventToRouteMap[nextEvent]) {
          navigate(eventToRouteMap[nextEvent]);
        } else {
          navigate('/e-kyc');
        }
      } else {
        setErrors({ api: response.Message || 'Failed to save banking details. Please try again.' });
      }
    } catch (error) {
      setErrors({ api: error.message || 'Failed to save banking details. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  if (checkingAuth) {
    return (
      <JourneyLayout journeyStep={6}>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-theme" />
        </div>
      </JourneyLayout>
    );
  }

  return (
    <JourneyLayout journeyStep={6}>
      <button
        onClick={() => navigate('/account-aggregator-redirect')}
        className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-theme mb-6 transition-colors bg-transparent border-none p-0"
      >
        <ArrowLeft className="h-4 w-4" />
        <span>Back</span>
      </button>

      <div className="text-center space-y-3 mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-theme/10 border border-theme/20 text-xs tracking-[0.3em] uppercase text-theme font-semibold">
          <CreditCard className="h-4 w-4" />
          Banking
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">Add your payout account</h1>
        <p className="text-gray-600">
          Snap Paisa transfers disbursals only to verified salary accounts.
        </p>
      </div>

      {errors.api && (
        <div className="mb-6 bg-red-50 border-2 border-red-200 rounded-2xl p-4 text-sm text-red-700">
          {errors.api}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {ifsc && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">IFSC Code</label>
            <input
              type="text"
              value={ifsc}
              readOnly
              className="w-full px-4 py-3 rounded-2xl bg-gray-100 border-2 border-gray-300 text-gray-600 cursor-not-allowed"
            />
          </div>
        )}

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Bank Branch <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={accountBranch}
            onChange={handleBranchChange}
            placeholder="Enter bank branch name"
            autoComplete="off"
            maxLength={100}
            className={`w-full px-4 py-3 rounded-2xl bg-gray-50 border-2 text-gray-900 placeholder:text-gray-400 focus:outline-none transition-all ${
              errors.branch
                ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                : 'border-gray-300 focus:border-theme focus:ring-2 focus:ring-theme/20'
            }`}
          />
          {errors.branch && (
            <p className="text-xs text-red-500 font-medium mt-1">{errors.branch}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Account Number <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="account_number"
            name="account_number"
            value={formData.account_number}
            onChange={handleChange}
            placeholder="Enter account number"
            maxLength={18}
            autoComplete="off"
            className={`w-full px-4 py-3 rounded-2xl bg-gray-50 border-2 text-gray-900 placeholder:text-gray-400 focus:outline-none transition-all ${
              errors.account_number
                ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                : 'border-gray-300 focus:border-theme focus:ring-2 focus:ring-theme/20'
            }`}
          />
          {errors.account_number && (
            <p className="text-xs text-red-500 font-medium mt-1">{errors.account_number}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Confirm Account Number <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="confirm_account_number"
            name="confirm_account_number"
            value={formData.confirm_account_number}
            onChange={handleChange}
            placeholder="Re-enter account number"
            maxLength={18}
            autoComplete="new-password"
            className={`w-full px-4 py-3 rounded-2xl bg-gray-50 border-2 text-gray-900 placeholder:text-gray-400 focus:outline-none transition-all ${
              errors.confirm_account_number
                ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                : 'border-gray-300 focus:border-theme focus:ring-2 focus:ring-theme/20'
            }`}
          />
          {errors.confirm_account_number && (
            <p className="text-xs text-red-500 font-medium mt-1">{errors.confirm_account_number}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-gradient-to-r from-theme to-theme2 text-white px-6 py-4 rounded-2xl font-semibold text-lg flex items-center justify-center gap-2 shadow-lg hover:shadow-xl disabled:opacity-50 transition-all"
        >
          {loading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Saving...</span>
            </>
          ) : (
            <>
              <span>Continue</span>
              <ArrowRight className="h-5 w-5" />
            </>
          )}
        </button>
      </form>
    </JourneyLayout>
  );
}

export default BankingDetails;

